import React, { useState, useEffect, useCallback } from 'react';
import API from '../services/api';
import { 
  Alert, 
  Button, 
  Card, 
  Typography, 
  Space, 
  Spin, 
  Empty, 
  Pagination, 
  Tooltip,
  Form,
  Input,
  Select,
  Table,
  Modal,
  Row,
  Col,
  Statistic,
  Tag,
  message
} from 'antd';
import { 
  FileTextOutlined, 
  ClockCircleOutlined, 
  TagOutlined, 
  HomeOutlined,
  BugOutlined,
  PlusOutlined,
  EyeOutlined,
  BarChartOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import DOMPurify from 'dompurify';
import './SystemUpdateLog.css';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const SystemUpdateLog = () => {
  // 系统更新日志相关状态
  const [version, setVersion] = useState('');
  const [versionType, setVersionType] = useState('');
  const [updateLog, setUpdateLog] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedCards, setExpandedCards] = useState(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const [totalVersions, setTotalVersions] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const pageSize = 3; // 每页显示3条
  
  // BUG反馈相关状态
  const [activeView, setActiveView] = useState('updateLog'); // 'updateLog' 或 'bugReport'
  const [submitLoading, setSubmitLoading] = useState(false);
  const [reports, setReports] = useState([]);
  const [statistics, setStatistics] = useState({});
  const [bugCurrentPage, setBugCurrentPage] = useState(1);
  const [totalReports, setTotalReports] = useState(0);
  const [selectedReport, setSelectedReport] = useState(null);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [activeBugTab, setActiveBugTab] = useState('submit');
  const [filters, setFilters] = useState({
    status: undefined,
    bugType: undefined
  });
  
  const [form] = Form.useForm();
  const navigate = useNavigate();

  // BUG类型选项
  const bugTypes = [
    { value: '功能异常', label: '功能异常', color: 'red' },
    { value: '界面问题', label: '界面问题', color: 'orange' },
    { value: '性能问题', label: '性能问题', color: 'blue' },
    { value: '数据错误', label: '数据错误', color: 'purple' },
    { value: '安全问题', label: '安全问题', color: 'volcano' },
    { value: '其他', label: '其他', color: 'default' }
  ];

  // 优先级选项
  const priorities = [
    { value: '低', label: '低', color: 'default' },
    { value: '中', label: '中', color: 'processing' },
    { value: '高', label: '高', color: 'warning' },
    { value: '紧急', label: '紧急', color: 'error' }
  ];

  // 状态选项
  const statuses = [
    { value: '待处理', label: '待处理', color: 'default' },
    { value: '处理中', label: '处理中', color: 'processing' },
    { value: '已修复', label: '已修复', color: 'success' },
    { value: '无法复现', label: '无法复现', color: 'warning' },
    { value: '不是BUG', label: '不是BUG', color: 'error' },
    { value: '已关闭', label: '已关闭', color: 'default' }
  ];

  useEffect(() => {
    const fetchSystemInfo = async () => {
      try {
        setLoading(true);
        const response = await API.get('/api/update/system-info', {
          params: {
            page: currentPage,
            limit: pageSize,
            paginated: 'true'
          }
        });
        setVersion(response.data.version || 'v1.0.0');
        setVersionType(response.data.versionType || 'release');
        setUpdateLog(response.data.updateLog || '暂无更新日志');
        setTotalVersions(response.data.totalVersions || 0);
        setTotalPages(response.data.totalPages || 0);
        setError('');
      } catch (error) {
        console.error('获取系统信息失败:', error);
        setError('获取系统信息失败，请稍后重试');
        setVersion('v1.0.0');
        setVersionType('release');
        setUpdateLog('暂无更新日志');
        setTotalVersions(0);
        setTotalPages(0);
      } finally {
        setLoading(false);
      }
    };

    fetchSystemInfo();
  }, [currentPage]);

  // BUG反馈相关函数
  // 获取浏览器信息
  const getBrowserInfo = () => {
    const userAgent = navigator.userAgent;
    let browserName = 'Unknown';
    let browserVersion = 'Unknown';

    if (userAgent.indexOf('Chrome') > -1) {
      browserName = 'Chrome';
      const match = userAgent.match(/Chrome\/(\d+)/);
      browserVersion = match ? match[1] : 'Unknown';
    } else if (userAgent.indexOf('Firefox') > -1) {
      browserName = 'Firefox';
      const match = userAgent.match(/Firefox\/(\d+)/);
      browserVersion = match ? match[1] : 'Unknown';
    } else if (userAgent.indexOf('Safari') > -1) {
      browserName = 'Safari';
      const match = userAgent.match(/Version\/(\d+)/);
      browserVersion = match ? match[1] : 'Unknown';
    } else if (userAgent.indexOf('Edge') > -1) {
      browserName = 'Edge';
      const match = userAgent.match(/Edge\/(\d+)/);
      browserVersion = match ? match[1] : 'Unknown';
    }

    return `${browserName} ${browserVersion}`;
  };

  // 获取设备信息
  const getDeviceInfo = () => {
    const platform = navigator.platform;
    const userAgent = navigator.userAgent;
    
    let os = 'Unknown';
    if (userAgent.indexOf('Windows') > -1) os = 'Windows';
    else if (userAgent.indexOf('Mac') > -1) os = 'macOS';
    else if (userAgent.indexOf('Linux') > -1) os = 'Linux';
    else if (userAgent.indexOf('Android') > -1) os = 'Android';
    else if (userAgent.indexOf('iOS') > -1) os = 'iOS';

    return `${os} (${platform})`;
  };

  // 提交BUG反馈
  const handleSubmit = async (values) => {
    try {
      setSubmitLoading(true);
      
      const submitData = {
        ...values,
        browserInfo: getBrowserInfo(),
        deviceInfo: getDeviceInfo(),
        pageUrl: window.location.href
      };

      await API.post('/api/bug-report/submit', submitData);
      
      message.success('BUG反馈提交成功！确认邮件已发送至您的邮箱。');
      form.resetFields();
      
      // 如果当前在列表页，刷新数据
      if (activeBugTab === 'list') {
        fetchReports();
        fetchStatistics();
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || '提交反馈失败，请稍后重试';
      message.error(errorMessage);
    } finally {
      setSubmitLoading(false);
    }
  };

  // 获取反馈列表
  const fetchReports = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      const params = {
        page,
        limit: 10,
        ...filters
      };
      
      const response = await API.get('/api/bug-report/my-reports', { params });
      
      setReports(response.data.reports);
      setTotalReports(response.data.totalReports);
      setBugCurrentPage(response.data.currentPage);
    } catch (error) {
      message.error('获取反馈列表失败');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  // 获取统计信息
  const fetchStatistics = useCallback(async () => {
    try {
      const response = await API.get('/api/bug-report/statistics');
      setStatistics(response.data);
    } catch (error) {
      message.error('获取统计信息失败');
    }
  }, []);

  // 获取反馈详情
  const fetchReportDetail = async (reportId) => {
    try {
      setLoading(true);
      const response = await API.get(`/api/bug-report/reports/${reportId}`);
      setSelectedReport(response.data);
      setDetailModalVisible(true);
    } catch (error) {
      message.error('获取反馈详情失败');
    } finally {
      setLoading(false);
    }
  };

  // 处理分页变化
  const handleBugPageChange = (page) => {
    fetchReports(page);
  };

  // 处理筛选变化
  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    fetchReports(1); // 重置到第一页
  };

  // 获取状态配置
  const getStatusConfig = (status) => {
    return statuses.find(s => s.value === status) || statuses[0];
  };

  // 获取类型配置
  const getTypeConfig = (type) => {
    return bugTypes.find(t => t.value === type) || bugTypes[0];
  };

  // 获取优先级配置
  const getPriorityConfig = (priority) => {
    return priorities.find(p => p.value === priority) || priorities[1];
  };

  // 格式化日期
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('zh-CN');
  };

  // 加载BUG反馈数据
  useEffect(() => {
    if (activeView === 'bugReport') {
      if (activeBugTab === 'list') {
        fetchReports();
        fetchStatistics();
      } else if (activeBugTab === 'statistics') {
        fetchStatistics();
      }
    }
  }, [activeView, activeBugTab, fetchReports, fetchStatistics]);

  // 解析和格式化更新日志
  const parseUpdateLog = (logText) => {
    if (!logText || logText === '暂无更新日志') {
      return [{ version: 'v1.0.0', date: '', versionType: 'release', content: '暂无更新日志' }];
    }

    // 先清理字符串，去除多余的换行符和空白
    const cleanedText = logText.trim();
    
    // 使用更准确的分割方式，处理多个连续换行符
    const sections = cleanedText.split(/\n\n+/).filter(section => section.trim());
    const parsedLogs = [];

    sections.forEach(section => {
      // 清理每个section，去除前后空白和换行符
      const cleanedSection = section.trim();
      const lines = cleanedSection.split('\n');
      const firstLine = lines[0].trim(); // 确保第一行没有前后空白
      
      // 匹配新格式：日期 - 版本号 (版本类型)
      const newFormatMatch = firstLine.match(/(\d{8})\s*-\s*(.+?)\s*\(([^)]+)\)/);
      // 匹配旧格式：日期 - 版本号
      const oldFormatMatch = firstLine.match(/(\d{8})\s*-\s*(.+)/);
      
      if (newFormatMatch) {
        const dateStr = newFormatMatch[1];
        const version = newFormatMatch[2].trim();
        const versionType = newFormatMatch[3].trim().toLowerCase();
        const content = lines.slice(1).map(line => line.trim()).join('\n').trim();
        
        // 格式化日期
        const formattedDate = `${dateStr.slice(0,4)}-${dateStr.slice(4,6)}-${dateStr.slice(6,8)}`;
        
        parsedLogs.push({
          version,
          date: formattedDate,
          versionType,
          content: content || '无更新内容'
        });
      } else if (oldFormatMatch) {
        // 向后兼容旧格式
        const dateStr = oldFormatMatch[1];
        const version = oldFormatMatch[2].trim();
        const content = lines.slice(1).map(line => line.trim()).join('\n').trim();
        
        // 格式化日期
        const formattedDate = `${dateStr.slice(0,4)}-${dateStr.slice(4,6)}-${dateStr.slice(6,8)}`;
        
        parsedLogs.push({
          version,
          date: formattedDate,
          versionType: 'release', // 默认为release类型
          content: content || '无更新内容'
        });
      } else {
        // 如果格式不匹配，检查是否为空或无效内容
        if (cleanedSection && cleanedSection.length > 0) {
          console.warn('无法解析的更新日志格式:', cleanedSection);
          parsedLogs.push({
            version: '格式错误',
            date: '',
            versionType: 'release',
            content: cleanedSection
          });
        }
      }
    });

    // 语义化版本排序函数
    const compareVersions = (a, b) => {
      // 移除版本号前的 'v' 前缀
      const cleanA = a.version.replace(/^v/, '');
      const cleanB = b.version.replace(/^v/, '');
      
      // 处理格式错误的版本
      if (cleanA === '格式错误') return 1;
      if (cleanB === '格式错误') return -1;
      
      // 分离版本号主体和后缀（如 -1, -2 等）
      const parseVersion = (version) => {
        const match = version.match(/^(\d+\.\d+\.\d+)(.*)$/);
        if (match) {
          const [, core, suffix] = match;
          const [major, minor, patch] = core.split('.').map(Number);
          return { major, minor, patch, suffix: suffix || '' };
        }
        // 如果不是标准的三段式版本号，尝试解析为数字
        const parts = version.split('.');
        return {
          major: parseInt(parts[0]) || 0,
          minor: parseInt(parts[1]) || 0,
          patch: parseInt(parts[2]) || 0,
          suffix: parts.slice(3).join('.') || ''
        };
      };
      
      const versionA = parseVersion(cleanA);
      const versionB = parseVersion(cleanB);
      
      // 比较主版本号
      if (versionA.major !== versionB.major) {
        return versionB.major - versionA.major;
      }
      
      // 比较次版本号
      if (versionA.minor !== versionB.minor) {
        return versionB.minor - versionA.minor;
      }
      
      // 比较修订版本号
      if (versionA.patch !== versionB.patch) {
        return versionB.patch - versionA.patch;
      }
      
      // 比较后缀：有后缀的版本号优先级更高
      if (!versionA.suffix && versionB.suffix) return 1;
      if (versionA.suffix && !versionB.suffix) return -1;
      if (!versionA.suffix && !versionB.suffix) return 0;
      
      // 都有后缀时，尝试数字比较
      const suffixA = versionA.suffix.replace(/^-/, ''); // 移除前导破折号
      const suffixB = versionB.suffix.replace(/^-/, ''); // 移除前导破折号
      
      // 检查是否都是数字后缀
      const numA = parseInt(suffixA);
      const numB = parseInt(suffixB);
      
      if (!isNaN(numA) && !isNaN(numB)) {
        // 数字后缀，数字大的优先级高
        return numB - numA;
      } else {
        // 非数字后缀，进行字符串比较（倒序）
        return suffixB.localeCompare(suffixA);
      }
    };

    // 按版本号排序（从高到低）
    const sortedLogs = parsedLogs.length > 0 ? parsedLogs.sort(compareVersions) : 
      [{ version: 'v1.0.0', date: '', versionType: 'release', content: '暂无更新日志' }];
    
    return sortedLogs;
  };

  const handleGoBack = () => {
    navigate('/user/dashboard');
  };

  // 处理分页变化
  const handlePageChange = (page) => {
    setCurrentPage(page);
    setExpandedCards(new Set()); // 切换页面时重置展开状态
  };

  // 切换卡片展开状态
  const toggleCardExpansion = (index) => {
    const newExpanded = new Set(expandedCards);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedCards(newExpanded);
  };

  // 判断内容是否需要截断
  const needsTruncation = (content) => {
    return content.length > 200;
  };

  // 检测内容是否包含HTML标签
  const containsHTML = (content) => {
    return /<[a-z][\s\S]*>/i.test(content);
  };

  // 截断内容
  const truncateContent = (content, maxLength = 200) => {
    if (content.length <= maxLength) return content;
    return content.substring(0, maxLength) + '...';
  };

  // 渲染内容（支持HTML）
  const renderContent = (content, isExpanded, needsExpansion) => {
    const displayContent = needsExpansion && !isExpanded ? 
      truncateContent(content) : 
      content;

    if (containsHTML(displayContent)) {
      // 在HTML处理前，先将换行符转换为<br>标签，但要避免重复转换
      const contentWithLineBreaks = displayContent.replace(/\n/g, '<br>');
      
      // 配置DOMPurify以允许常用的安全标签和属性
      const cleanHTML = DOMPurify.sanitize(contentWithLineBreaks, {
        ALLOWED_TAGS: ['p', 'div', 'span', 'br', 'strong', 'b', 'em', 'i', 'u', 'small', 'mark', 'del', 'ins', 'sub', 'sup'],
        ALLOWED_ATTR: ['style', 'class'],
        ALLOWED_URI_REGEXP: /^(?:(?:(?:f|ht)tps?|mailto|tel|callto|cid|xmpp):|[^a-z]|[a-z+.-]+(?:[^a-z+.-:]|$))/i
      });
      
      return (
        <div 
          style={{ 
            fontSize: '13px',
            lineHeight: '1.6'
          }}
          dangerouslySetInnerHTML={{ __html: cleanHTML }}
        />
      );
    } else {
      return (
        <Text style={{ 
          whiteSpace: 'pre-wrap', 
          fontSize: '13px',
          display: 'block',
          lineHeight: '1.6'
        }}>
          {displayContent}
        </Text>
      );
    }
  };

  // 获取版本类型的显示配置
  const getVersionTypeConfig = (versionType) => {
    const configs = {
      alpha: { label: 'Alpha', color: '#ff4d4f', icon: '🔥', bgColor: '#fff2f0', description: '早期开发版本，功能不完整，可能存在较多问题' },
      beta: { label: 'Beta', color: '#fa8c16', icon: '⚡', bgColor: '#fff7e6', description: '功能基本完成，正在进行测试的版本' },
      rc: { label: 'RC', color: '#1890ff', icon: '🚀', bgColor: '#e6f7ff', description: '即将正式发布的候选版本，功能完整' },
      sp: { label: 'SP', color: '#722ed1', icon: '🔧', bgColor: '#f9f0ff', description: '基于正式版的补丁包，包含bug修复和小幅改进' },
      release: { label: 'Release', color: '#52c41a', icon: '✅', bgColor: '#f6ffed', description: '经过充分测试的正式发布版本' }
    };
    return configs[versionType?.toLowerCase()] || configs.release;
  };

  const renderUpdateLogs = () => {
    const logs = parseUpdateLog(updateLog);
    
    if (logs.length === 1 && logs[0].content === '暂无更新日志') {
      return (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            <div>
              <Text type="secondary">暂无更新日志</Text>
              <br />
              <Text type="secondary" style={{ fontSize: '12px' }}>
                请联系管理员获取最新版本信息
              </Text>
            </div>
          }
        />
      );
    }

    return logs.map((log, index) => {
      const isExpanded = expandedCards.has(index);
      const needsExpansion = needsTruncation(log.content);
      const typeConfig = getVersionTypeConfig(log.versionType);
      
      // 判断是否为当前版本：比较版本号和版本类型
      const isCurrentVersion = (log.version === version || 
        (version.startsWith('v') ? log.version === version : log.version === `v${version}`) ||
        (log.version.startsWith('v') ? log.version.slice(1) === version : `v${log.version}` === version)) &&
        log.versionType === versionType;

      return (
        <Card
          key={index}
          className={`update-log-card ${isCurrentVersion ? 'current-version' : 'history-version'}`}
          size="small"
        >
          <div className="version-header">
            <div className="version-info">
              <span className={`version-badge ${isCurrentVersion ? 'current' : 'history'}`}>
                <TagOutlined style={{ marginRight: '4px' }} />
                {log.version}
              </span>
              
              {/* 版本类型徽章 */}
              <Tooltip title={typeConfig.description} placement="top">
                <span style={{
                  background: typeConfig.bgColor,
                  color: typeConfig.color,
                  padding: '2px 6px',
                  borderRadius: '10px',
                  fontSize: '11px',
                  fontWeight: 'bold',
                  border: `1px solid ${typeConfig.color}`,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '2px',
                  cursor: 'help'
                }}>
                  <span>{typeConfig.icon}</span>
                  {typeConfig.label}
                </span>
              </Tooltip>
              
              {log.date && (
                <span className="date-info">
                  <ClockCircleOutlined style={{ marginRight: '4px' }} />
                  {log.date}
                </span>
              )}
              
              {isCurrentVersion && (
                <span className="current-indicator">
                  ⭐ 当前版本
                </span>
              )}
            </div>
          </div>
          
          <div className="update-content">
            {renderContent(log.content, isExpanded, needsExpansion)}
            
            {needsExpansion && (
              <Button
                type="link"
                size="small"
                onClick={() => toggleCardExpansion(index)}
                style={{ 
                  padding: 0,
                  marginTop: '8px',
                  height: 'auto'
                }}
              >
                {isExpanded ? '收起' : '展开更多'}
              </Button>
            )}
          </div>
        </Card>
      );
    });
  };

  // BUG反馈的渲染函数
  // 表格列定义
  const bugColumns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
      render: (text) => (
        <Text code style={{ fontSize: '12px' }}>
          {text.slice(-8)}
        </Text>
      )
    },
    {
      title: '标题',
      dataIndex: 'title',
      key: 'title',
      ellipsis: true,
      render: (text) => (
        <Tooltip title={text}>
          <Text strong>{text}</Text>
        </Tooltip>
      )
    },
    {
      title: '类型',
      dataIndex: 'bugType',
      key: 'bugType',
      width: 100,
      render: (type) => {
        const config = getTypeConfig(type);
        return <Tag color={config.color}>{config.label}</Tag>;
      }
    },
    {
      title: '优先级',
      dataIndex: 'priority',
      key: 'priority',
      width: 80,
      render: (priority) => {
        const config = getPriorityConfig(priority);
        return <Tag color={config.color}>{config.label}</Tag>;
      }
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status) => {
        const config = getStatusConfig(status);
        return <Tag color={config.color}>{config.label}</Tag>;
      }
    },
    {
      title: '提交时间',
      dataIndex: 'submittedAt',
      key: 'submittedAt',
      width: 150,
      render: (date) => formatDate(date)
    },
    {
      title: '操作',
      key: 'action',
      width: 80,
      render: (_, record) => (
        <Button
          type="link"
          size="small"
          icon={<EyeOutlined />}
          onClick={() => fetchReportDetail(record.id)}
        >
          详情
        </Button>
      )
    }
  ];

  // 渲染提交表单
  const renderSubmitForm = () => (
    <Card title="提交BUG反馈" style={{ marginBottom: 16 }}>
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          priority: '中',
          bugType: '功能异常'
        }}
      >
        <Row gutter={16}>
          <Col span={24}>
            <Form.Item
              name="title"
              label="BUG标题"
              rules={[{ required: true, message: '请输入BUG标题' }]}
            >
              <Input placeholder="请简要描述遇到的问题" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="bugType"
              label="BUG类型"
              rules={[{ required: true, message: '请选择BUG类型' }]}
            >
              <Select placeholder="请选择BUG类型">
                {bugTypes.map(type => (
                  <Option key={type.value} value={type.value}>
                    <Tag color={type.color}>{type.label}</Tag>
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="priority"
              label="优先级"
              rules={[{ required: true, message: '请选择优先级' }]}
            >
              <Select placeholder="请选择优先级">
                {priorities.map(priority => (
                  <Option key={priority.value} value={priority.value}>
                    <Tag color={priority.color}>{priority.label}</Tag>
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          name="description"
          label="详细描述"
          rules={[{ required: true, message: '请详细描述遇到的问题' }]}
        >
          <TextArea
            rows={6}
            placeholder="请详细描述遇到的问题，包括：&#10;1. 操作步骤&#10;2. 预期结果&#10;3. 实际结果&#10;4. 错误信息（如有）"
          />
        </Form.Item>

        <Form.Item>
          <Space>
            <Button
              type="primary"
              htmlType="submit"
              loading={submitLoading}
              icon={<PlusOutlined />}
            >
              提交反馈
            </Button>
            <Button onClick={() => form.resetFields()}>
              重置
            </Button>
          </Space>
        </Form.Item>
      </Form>

      <Alert
        message="提示"
        description="提交反馈后，我们会通过邮件通知您处理进度。系统会自动收集您的浏览器和设备信息以帮助定位问题。"
        type="info"
        showIcon
        style={{ marginTop: 16 }}
      />
    </Card>
  );

  // 渲染反馈列表
  const renderReportList = () => (
    <div>
      {/* 筛选器 */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <Row gutter={16} align="middle">
          <Col>
            <Text strong>筛选条件：</Text>
          </Col>
          <Col>
            <Select
              placeholder="状态"
              allowClear
              style={{ width: 120 }}
              value={filters.status}
              onChange={(value) => handleFilterChange('status', value)}
            >
              {statuses.map(status => (
                <Option key={status.value} value={status.value}>
                  <Tag color={status.color}>{status.label}</Tag>
                </Option>
              ))}
            </Select>
          </Col>
          <Col>
            <Select
              placeholder="类型"
              allowClear
              style={{ width: 120 }}
              value={filters.bugType}
              onChange={(value) => handleFilterChange('bugType', value)}
            >
              {bugTypes.map(type => (
                <Option key={type.value} value={type.value}>
                  <Tag color={type.color}>{type.label}</Tag>
                </Option>
              ))}
            </Select>
          </Col>
        </Row>
      </Card>

      {/* 反馈列表 */}
      <Card title="我的反馈列表">
        <Table
          columns={bugColumns}
          dataSource={reports}
          rowKey="id"
          loading={loading}
          pagination={{
            current: bugCurrentPage,
            total: totalReports,
            pageSize: 10,
            showTotal: (total, range) => 
              `第 ${range[0]}-${range[1]} 条，共 ${total} 条记录`,
            showSizeChanger: false,
            onChange: handleBugPageChange
          }}
          size="small"
        />
      </Card>
    </div>
  );

  // 渲染统计信息
  const renderStatistics = () => (
    <div>
      <Row gutter={16}>
        <Col span={6}>
          <Card>
            <Statistic
              title="总反馈数"
              value={statistics.totalReports}
              prefix={<BugOutlined />}
            />
          </Card>
        </Col>
        <Col span={18}>
          <Card title="状态分布">
            <Row gutter={16}>
              {Object.entries(statistics.statusStatistics || {}).map(([status, count]) => {
                const config = getStatusConfig(status);
                return (
                  <Col span={4} key={status}>
                    <Statistic
                      title={<Tag color={config.color}>{status}</Tag>}
                      value={count}
                    />
                  </Col>
                );
              })}
            </Row>
          </Card>
        </Col>
      </Row>

      <Card title="类型分布" style={{ marginTop: 16 }}>
        <Row gutter={16}>
          {Object.entries(statistics.typeStatistics || {}).map(([type, count]) => {
            const config = getTypeConfig(type);
            return (
              <Col span={4} key={type}>
                <Statistic
                  title={<Tag color={config.color}>{type}</Tag>}
                  value={count}
                />
              </Col>
            );
          })}
        </Row>
      </Card>
    </div>
  );

  // 渲染BUG反馈内容
  const renderBugReportContent = () => {
    if (activeBugTab === 'submit') {
      return renderSubmitForm();
    } else if (activeBugTab === 'list') {
      return renderReportList();
    } else if (activeBugTab === 'statistics') {
      return renderStatistics();
    }
  };

  return (
    <div className="system-update-log-page">
      <div className="page-container">
        {/* 页面头部 */}
        <div className="page-header">
          <div className="header-content">
            <div className="header-left">
              <Button 
                type="text" 
                icon={<HomeOutlined />} 
                onClick={handleGoBack}
                className="back-button"
              >
                返回
              </Button>
            </div>
            <div className="header-center">
              {activeView === 'updateLog' ? (
                <>
                  <FileTextOutlined style={{ marginRight: '8px', color: '#3b82f6' }} />
                  <Title level={2} style={{ margin: 0 }}>
                    系统更新日志
                  </Title>
                  <span className="current-version-badge">
                    当前版本 {version}
                    {versionType && (() => {
                      const typeConfig = getVersionTypeConfig(versionType);
                      return (
                        <Tooltip title={typeConfig.description} placement="bottom">
                          <span style={{
                            background: typeConfig.bgColor,
                            color: typeConfig.color,
                            padding: '1px 4px',
                            borderRadius: '8px',
                            fontSize: '10px',
                            fontWeight: 'bold',
                            border: `1px solid ${typeConfig.color}`,
                            marginLeft: '4px',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '1px',
                            cursor: 'help'
                          }}>
                            <span style={{ fontSize: '8px' }}>{typeConfig.icon}</span>
                            {typeConfig.label}
                          </span>
                        </Tooltip>
                      );
                    })()}
                  </span>
                </>
              ) : (
                <>
                  <BugOutlined style={{ marginRight: '8px', color: '#ff4d4f' }} />
                  <Title level={2} style={{ margin: 0 }}>
                    BUG反馈
                  </Title>
                </>
              )}
            </div>
            <div className="header-right">
              <Space>
                <Button 
                  type={activeView === 'updateLog' ? 'primary' : 'default'}
                  icon={<FileTextOutlined />}
                  onClick={() => setActiveView('updateLog')}
                >
                  更新日志
                </Button>
                <Button 
                  type={activeView === 'bugReport' ? 'primary' : 'default'}
                  icon={<BugOutlined />}
                  onClick={() => setActiveView('bugReport')}
                  style={activeView === 'bugReport' ? { backgroundColor: '#ff4d4f', borderColor: '#ff4d4f' } : {}}
                >
                  BUG反馈
                </Button>
              </Space>
            </div>
          </div>
        </div>

        {/* 错误提示 */}
        {error && (
          <Alert
            message="加载失败"
            description={error}
            type="error"
            showIcon
            style={{ marginBottom: '20px' }}
            action={
              <Button size="small" onClick={() => window.location.reload()}>
                重试
              </Button>
            }
          />
        )}

        {/* 主要内容区域 */}
        {activeView === 'updateLog' ? (
          /* 系统更新日志内容 */
          <>
            {/* 加载状态 */}
            {loading ? (
              <div className="loading-container">
                <Spin size="large" />
                <Text style={{ 
                  marginTop: '16px', 
                  display: 'block', 
                  textAlign: 'center'
                }}>
                  正在加载更新日志...
                </Text>
              </div>
            ) : (
              /* 更新日志内容 */
              <div className="logs-container">
                <div className="logs-header">
                  <Space align="center">
                    <Text strong style={{ fontSize: '16px' }}>
                      版本历史记录
                    </Text>
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      按发布时间倒序排列，每页显示 {pageSize} 个版本
                    </Text>
                  </Space>
                </div>
                
                <div className="logs-content">
                  {renderUpdateLogs()}
                </div>
                
                {/* 分页组件 */}
                {totalPages > 1 && (
                  <div style={{ 
                    textAlign: 'center', 
                    marginTop: '20px',
                    paddingTop: '16px',
                    borderTop: '1px solid #f0f0f0'
                  }}>
                    <Pagination
                      current={currentPage}
                      total={totalVersions}
                      pageSize={pageSize}
                      showTotal={(total, range) => 
                        `第 ${range[0]}-${range[1]} 条，共 ${total} 个版本`
                      }
                      showSizeChanger={false}
                      onChange={handlePageChange}
                      size="small"
                    />
                  </div>
                )}
              </div>
            )}
          </>
        ) : (
          /* BUG反馈内容 */
          <div className="bug-report-container">
            {/* BUG反馈标签页 */}
            <div style={{ marginBottom: '20px' }}>
              <Space>
                <Button 
                  type={activeBugTab === 'submit' ? 'primary' : 'default'}
                  icon={<PlusOutlined />}
                  onClick={() => setActiveBugTab('submit')}
                >
                  提交反馈
                </Button>
                <Button 
                  type={activeBugTab === 'list' ? 'primary' : 'default'}
                  icon={<BugOutlined />}
                  onClick={() => setActiveBugTab('list')}
                >
                  我的反馈
                </Button>
                <Button 
                  type={activeBugTab === 'statistics' ? 'primary' : 'default'}
                  icon={<BarChartOutlined />}
                  onClick={() => setActiveBugTab('statistics')}
                >
                  统计信息
                </Button>
              </Space>
            </div>

            {/* BUG反馈内容 */}
            {renderBugReportContent()}

            {/* 详情弹窗 */}
            <Modal
              title="反馈详情"
              open={detailModalVisible}
              onCancel={() => setDetailModalVisible(false)}
              footer={[
                <Button key="close" onClick={() => setDetailModalVisible(false)}>
                  关闭
                </Button>
              ]}
              width={800}
            >
              {selectedReport && (
                <div>
                  <Row gutter={16}>
                    <Col span={16}>
                      <Title level={4}>{selectedReport.title}</Title>
                    </Col>
                    <Col span={8} style={{ textAlign: 'right' }}>
                      <Space orientation="vertical" size="small">
                        <Tag color={getTypeConfig(selectedReport.bugType).color}>
                          {selectedReport.bugType}
                        </Tag>
                        <Tag color={getPriorityConfig(selectedReport.priority).color}>
                          {selectedReport.priority}
                        </Tag>
                        <Tag color={getStatusConfig(selectedReport.status).color}>
                          {selectedReport.status}
                        </Tag>
                      </Space>
                    </Col>
                  </Row>

                  <div style={{ marginTop: 16 }}>
                    <Text strong>问题描述：</Text>
                    <Paragraph style={{ marginTop: 8, whiteSpace: 'pre-wrap' }}>
                      {selectedReport.description}
                    </Paragraph>
                  </div>

                  {selectedReport.processingNotes && (
                    <div style={{ marginTop: 16 }}>
                      <Text strong>处理备注：</Text>
                      <Paragraph style={{ marginTop: 8, whiteSpace: 'pre-wrap' }}>
                        {selectedReport.processingNotes}
                      </Paragraph>
                    </div>
                  )}

                  {selectedReport.resolutionNotes && (
                    <div style={{ marginTop: 16 }}>
                      <Text strong>解决方案：</Text>
                      <Paragraph style={{ marginTop: 8, whiteSpace: 'pre-wrap' }}>
                        {selectedReport.resolutionNotes}
                      </Paragraph>
                    </div>
                  )}

                  <div style={{ marginTop: 16 }}>
                    <Row gutter={16}>
                      <Col span={12}>
                        <Text strong>浏览器信息：</Text>
                        <br />
                        <Text type="secondary">{selectedReport.browserInfo || '-'}</Text>
                      </Col>
                      <Col span={12}>
                        <Text strong>设备信息：</Text>
                        <br />
                        <Text type="secondary">{selectedReport.deviceInfo || '-'}</Text>
                      </Col>
                    </Row>
                  </div>

                  <div style={{ marginTop: 16 }}>
                    <Row gutter={16}>
                      <Col span={8}>
                        <Text strong>提交时间：</Text>
                        <br />
                        <Text type="secondary">{formatDate(selectedReport.submittedAt)}</Text>
                      </Col>
                      <Col span={8}>
                        <Text strong>最后更新：</Text>
                        <br />
                        <Text type="secondary">{formatDate(selectedReport.lastUpdatedAt)}</Text>
                      </Col>
                      {selectedReport.resolvedAt && (
                        <Col span={8}>
                          <Text strong>解决时间：</Text>
                          <br />
                          <Text type="secondary">{formatDate(selectedReport.resolvedAt)}</Text>
                        </Col>
                      )}
                    </Row>
                  </div>

                  {selectedReport.assignedTo && (
                    <div style={{ marginTop: 16 }}>
                      <Text strong>处理人员：</Text>
                      <br />
                      <Text type="secondary">
                        {typeof selectedReport.assignedTo === 'string' 
                          ? selectedReport.assignedTo 
                          : (selectedReport.assignedTo.displayName || selectedReport.assignedTo.username || selectedReport.assignedTo.email || 'Unknown')
                        }
                      </Text>
                    </div>
                  )}
                </div>
              )}
            </Modal>
          </div>
        )}
      </div>
    </div>
  );
};

export default SystemUpdateLog; 